import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { show } from "store/reducers/notifications.reducer";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Button, Menu, Box, Typography, Switch } from "@mui/material/";
import ProtectedComponent from "components/common/ProtectedComponent";
import customAxios from "lib/axios";

// (Opcional) helper no usado actualmente, lo dejo migrado por si se usa en el futuro
const isShared = async (id, userId) => {
  const { data } = await customAxios.get(`liveWallet/isOnLiveRobot/${id}`);
  return data?.success ? data?.message === 1 : false;
};

const PopUpMenu = ({
  id,
  title,
  userId,
  anchor,
  handleClose,
  handleLoad,
  handleErrorRobot,
  shared,
  activeAPI,
  robotAlwaysActive,
  showConnector = true, // 👈 NUEVO
  hideOptimization = false, // ⬅️ NUEVO
}) => {
  const { t } = useTranslation(["robots", "errors"]);
  const [isLoading, setIsLoading] = useState(false);
  const [clientShared, setClientShared] = useState(shared);
  const [clientAPIactivated, setClientAPIactivated] = useState(activeAPI);
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    setClientAPIactivated(activeAPI);
  }, [activeAPI]);

  // ------- Auxiliares (todas con customAxios) -------

  const checkMT4AccountExists = async (uid) => {
    try {
      const { data } = await customAxios.get(`robots/existsUserAccount`);
      return data;
    } catch (e) {
      throw new Error(`Error al verificar la cuenta MT4: ${e?.message || e}`);
    }
  };

  const fetchSharedRobot = async (robotId) => {
    try {
      const { data } = await customAxios.get(`sharedrobots/${robotId}`);
      return data;
    } catch (e) {
      throw new Error(
        `Error al obtener el robot compartido: ${e?.message || e}`
      );
    }
  };

  const formatStrategies = (strategies) =>
    strategies.map((strategy) => ({
      id: Number(strategy.strategyId),
      type: "my_strategies",
    }));

  const fetchStrategies = async (robotId) => {
    try {
      const { data } = await customAxios.get(`wallet/${robotId}`);
      const formattedStrategies = formatStrategies(data);
      return formattedStrategies;
    } catch (e) {
      throw new Error(`Error al obtener las estrategias: ${e?.message || e}`);
    }
  };

  const createNewRobot = async (payload) => {
    try {
      const res = await customAxios.post(`robots`, payload);
      return res.data;
    } catch (e) {
      throw new Error(`Error al crear el robot: ${e?.message || e}`);
    }
  };

  const createLiveWallet = async (payload) => {
    try {
      const res = await customAxios.post(`livewallet`, payload);
      return res.data;
    } catch (e) {
      throw new Error(`Error al crear el Live Wallet: ${e?.message || e}`);
    }
  };

  const duplicateStrategy = async (strategyId) => {
    try {
      const response = await customAxios.get(
        `strategies/${strategyId}/duplicate`
      );
      if (response.data?.success) {
        return response.data.strategy;
      }
      throw new Error(
        response.data?.message || "No se pudo duplicar la estrategia"
      );
    } catch (error) {
      console.error(`Error al duplicar la estrategia ${strategyId}:`, error);
      throw error;
    }
  };

  const deactivateRobot = async (robotId, uid) => {
    // obtener ID de mi clon
    const { data: robotRes } = await customAxios.get(
      `robots/getCloned/${robotId}`
    );

    // estrategias de la wallet del clon
    const { data: strats } = await customAxios.get(`wallet/${robotRes.id}`);

    // borrar cada estrategia
    for (const s of strats) {
      await customAxios.delete(`strategies/${s.strategyId}`);
    }

    // comprobar si API está activada
    const { data: responseShared } = await customAxios.get(
      `liveWallet/isAPIActivated/${robotRes.id}`
    );

    if (responseShared?.message === "0") {
      await customAxios.get(`liveWallet/delete/${robotRes.id}`);
      await customAxios.delete(`robots/${robotRes.id}`);
    } else {
      throw new Error("Remueve el robot en el panel de Conector");
    }
  };

  // ------- Flujos principales -------

  const activateRobot = async () => {
    // Verificar existencia de la cuenta MT4
    const resAccount = await checkMT4AccountExists(userId);
    if (!resAccount?.exists) {
      throw new Error(t("ErrorAccountMT4"));
    }

    const robot = await fetchSharedRobot(id);
    if (!robot) {
      throw new Error("El robot no existe");
    }

    // Obtener y formatear estrategias
    const strategies = await fetchStrategies(robot.robotId);
    if (!strategies || strategies.length === 0) {
      throw new Error("No se encontraron estrategias para el robot");
    }

    const duplicatedStrategies = await Promise.all(
      strategies.map(async (strategy) => {
        const duplicatedStrategy = await duplicateStrategy(strategy.id);
        return {
          id: duplicatedStrategy.id,
          type: strategy.type || duplicatedStrategy.type,
        };
      })
    );

    // Crear nuevo robot
    const dataNewRobot = {
      user: userId,
      title: `${robot.title}_from sR`,
      strategies: duplicatedStrategies,
      cloned_id: robot.id,
    };
    const newRobot = await createNewRobot(dataNewRobot);

    // Crear Live Wallet
    const dataLiveWallet = {
      id: newRobot.id,
      user_id: userId,
      name: robot.title,
      more_info_url: "none",
      image: "none",
      operable: "Y",
      group: "-1",
      is_shared: clientShared, // uso el estado local
    };
    await createLiveWallet(dataLiveWallet);

    setClientAPIactivated("Y");
    return newRobot.id;
  };

  const deactivateRobotHandler = async () => {
    await deactivateRobot(id, userId);
    setClientAPIactivated("N");
  };

  // ------- Evento principal -------

  const handleAPIActivation = async (event) => {
    setIsLoading(true);
    const isChecked = event.target.checked;
    let robotId;

    try {
      if (isChecked) {
        robotId = await activateRobot();
      } else {
        await deactivateRobotHandler();
      }
    } catch (error) {
      console.error(error);
      handleErrorRobot(error.message);
    } finally {
      setIsLoading(false);

      if (clientAPIactivated === "N") {
        dispatch(show({ type: "warning", msg: t("tradingRobots:redirected") }));
        history.push("/tradingrobots/main", { activatedRobotId: robotId });
      }
    }
  };

  // ------- Render -------

  return (
    <Menu
      id="customized-menu"
      anchorEl={anchor}
      elevation={0}
      getContentAnchorEl={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      open={Boolean(anchor)}
      onClose={handleClose}
      bgcolor="#253E66"
      PaperProps={{
        sx: {
          borderRadius: "5px",
          backgroundColor: "#253E66",
        },
      }}
      MenuListProps={{
        sx: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* 🔴 Solo mostramos el conector si showConnector === true */}
      {showConnector && (
        <ProtectedComponent
          iconTop="-30px"
          iconRight="2px"
          iconSize="20"
          iconColor="grey"
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-around"
            px={2}
          >
            <Switch
              disableRipple
              disableTouchRipple
              sx={{
                "& .MuiSwitch-track": {
                  height: 16,
                  width: 30,
                  borderRadius: "10px",
                },
                "& .MuiSwitch-thumb": {
                  color: "white",
                  boxShadow: "none",
                  height: 12,
                  width: 12,
                },
                "& .MuiSwitch-switchBase": {
                  top: 5,
                  left: 5,
                  "&.Mui-disabled": {
                    cursor: "not-allowed",
                  },
                  "&.Mui-checked": {
                    left: -1,
                    "& +.MuiSwitch-track": {
                      color: "red",
                      opacity: "1 !important",
                    },
                  },
                },
              }}
              disabled={isLoading || robotAlwaysActive}
              checked={clientAPIactivated === "Y" || robotAlwaysActive}
              onChange={handleAPIActivation}
            />
            <Typography color="white" variant="body2">
              {t("ActAPI")}
            </Typography>
          </Box>
        </ProtectedComponent>
      )}

      {/* BOTÓN DE VALIDACIÓN DE ROBOT */}
      <Button
        onClick={handleLoad(id, "/robotvalidation")}
        variant="text"
        color="secondary"
      >
        {t("OpenValidator")}
      </Button>

      {/* BOTÓN DE OPTIMIZACIÓN (oculto si hideOptimization === true) */}

      {/* De moment no volem el boto d'otpmització aqui als robots */}
      {/* {!hideOptimization && (
        <Button
          onClick={handleLoad(id, "/optimization")}
          variant="text"
          color="secondary"
        >
          {t("OpenOptimization")}
        </Button>
      )} */}
    </Menu>
  );
};

PopUpMenu.propTypes = {
  t: PropTypes.func,
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  anchor: PropTypes.objectOf(PropTypes.any).isRequired,
  handleClose: PropTypes.func.isRequired,
  handleDuplicate: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleLoad: PropTypes.func.isRequired,
  shared: PropTypes.number.isRequired,
  activeAPI: PropTypes.string.isRequired,
  handleErrorRobot: PropTypes.func.isRequired,
  robotAlwaysActive: PropTypes.bool.isRequired,
  showConnector: PropTypes.bool,
  hideOptimization: PropTypes.bool,
};

export default PopUpMenu;
